import React, { useState } from 'react';
import { Check, ArrowRight, Crown, Star, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { pricingInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const features = [
    'Custom Website Design',
    'Responsive Development',
    'SEO Optimization & Management',
    'Performance Optimization',
    'Cross-browser Compatibility',
    'Premium Hosting Setup',
    'Contact Form Integration',
    'Advanced Security Features',
    '30 Days Post-Launch Support',
    'Free Domain for 1 Year',
    'Google Analytics Setup',
    'Content Management System'
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleClaimOffer = () => {
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send form data to backend
      const response = await fetch(`${BACKEND_URL}/api/pricing-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormSubmitted(true);
        
        // After 1 second, redirect to PayPal
        setTimeout(() => {
          window.open(pricingInfo.paypalLink, '_blank');
        }, 1000);
      } else {
        console.error('Failed to submit pricing form');
        // Still redirect to PayPal even if backend fails
        setFormSubmitted(true);
        setTimeout(() => {
          window.open(pricingInfo.paypalLink, '_blank');
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting pricing form:', error);
      // Still redirect to PayPal even if backend fails
      setFormSubmitted(true);
      setTimeout(() => {
        window.open(pricingInfo.paypalLink, '_blank');
      }, 1000);
    }
  };

  const handlePayPalPayment = () => {
    // Direct PayPal payment without form
    window.open(pricingInfo.paypalLink, '_blank');
  };

  return (
    <section id="pricing" className="py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-100/30 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-blue-500 font-bold text-sm uppercase tracking-wider bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
              <Crown className="w-4 h-4 inline mr-2" />
              Limited Time Offer
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            Transparent <span className="text-blue-500">Pricing</span>
          </h2>
          <p className="text-xl text-slate-600">
            Quality website solutions at competitive prices. No hidden fees, no surprises.
          </p>
        </div>

        {/* Special Offer Banner */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl p-10 text-white shadow-2xl text-center">
            <Star className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-4xl font-black mb-4">
              Special Launch Offer: First {pricingInfo.offerLimit} Clients Only!
            </h3>
            <div className="flex items-center justify-center space-x-8 mb-4">
              <div className="text-center">
                <p className="text-lg opacity-90 line-through">Regular Price</p>
                <p className="text-3xl font-bold">{pricingInfo.currency}{pricingInfo.regularPrice}</p>
              </div>
              <div className="text-6xl font-black">→</div>
              <div className="text-center">
                <p className="text-lg opacity-90">Your Price</p>
                <p className="text-6xl font-black">{pricingInfo.currency}{pricingInfo.specialOfferPrice}</p>
              </div>
            </div>
            <p className="text-2xl font-bold">Save {pricingInfo.currency}{pricingInfo.regularPrice - pricingInfo.specialOfferPrice}! ({Math.round((1 - pricingInfo.specialOfferPrice / pricingInfo.regularPrice) * 100)}% OFF)</p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 mb-12">
          {/* Special Offer Card - WITH FORM BEFORE PAYMENT */}
          <Card className="border-4 border-blue-500 shadow-2xl overflow-hidden relative transform hover:scale-105 transition-all duration-500">
            <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded-full animate-pulse">
              🔥 HOT DEAL
            </div>
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-10 text-white">
                <h3 className="text-3xl font-black mb-2">Launch Special</h3>
                <p className="text-lg font-bold mb-6">First {pricingInfo.offerLimit} Clients</p>
                <div className="mb-8">
                  <div className="text-6xl font-black mb-2">{pricingInfo.currency}{pricingInfo.specialOfferPrice}</div>
                  <p className="text-xl line-through opacity-75">Was {pricingInfo.currency}{pricingInfo.regularPrice}</p>
                </div>

                {/* Show Form or Button */}
                {!showForm ? (
                  <>
                    <Button
                      onClick={handleClaimOffer}
                      className="bg-white text-blue-500 hover:bg-blue-50 w-full py-6 text-lg font-black rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Claim This Offer Now!
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <p className="text-xs mt-3 text-center opacity-75">Secure payment via PayPal</p>
                  </>
                ) : (
                  <div className="bg-white rounded-xl p-6">
                    {!formSubmitted ? (
                      <>
                        <h4 className="text-blue-500 font-black text-xl mb-4">Tell Us About Your Project</h4>
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                          <div>
                            <label className="flex items-center text-slate-700 font-semibold mb-2 text-sm">
                              <User className="w-4 h-4 mr-2" />
                              Full Name *
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-slate-900"
                              placeholder="John Doe"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-slate-700 font-semibold mb-2 text-sm">
                              <Mail className="w-4 h-4 mr-2" />
                              Email Address *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-slate-900"
                              placeholder="john@example.com"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-slate-700 font-semibold mb-2 text-sm">
                              <Phone className="w-4 h-4 mr-2" />
                              Phone Number *
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-slate-900"
                              placeholder="0415365783"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-slate-700 font-semibold mb-2 text-sm">
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Project Details (Optional)
                            </label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              rows="3"
                              className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-slate-900"
                              placeholder="Tell us about your project..."
                            />
                          </div>
                          <Button
                            type="submit"
                            className="bg-blue-500 hover:bg-blue-600 text-white w-full py-4 text-lg font-black rounded-lg shadow-lg transition-all duration-300"
                          >
                            Proceed to Payment
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Check className="w-10 h-10 text-white" strokeWidth={3} />
                        </div>
                        <h4 className="text-blue-500 font-black text-xl mb-2">Thank You!</h4>
                        <p className="text-slate-600 font-semibold">Redirecting to PayPal...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Regular Pricing Card */}
          <Card className="border-2 border-slate-200 shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
            <CardContent className="p-0">
              <div className="p-10 bg-white">
                <h3 className="text-3xl font-black text-slate-900 mb-2">Standard Rate</h3>
                <p className="text-lg font-bold text-slate-600 mb-6">Regular Pricing</p>
                <div className="mb-8">
                  <div className="text-6xl font-black text-slate-900 mb-2">{pricingInfo.currency}{pricingInfo.regularPrice}</div>
                  <p className="text-xl text-slate-600">starting</p>
                </div>
                <Button
                  onClick={scrollToContact}
                  className="border-2 border-blue-500 bg-white text-blue-500 hover:bg-blue-50 w-full py-6 text-lg font-black rounded-xl shadow-lg transition-all duration-300"
                >
                  Get Custom Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features List */}
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-blue-200 shadow-xl bg-white">
            <CardContent className="p-10">
              <div className="text-center mb-8">
                <Crown className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h4 className="text-3xl font-black text-slate-900 mb-3">What's Included</h4>
                <p className="text-lg text-slate-600">Everything you need for a world-class web presence</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors duration-300">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 font-semibold">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Pricing;